import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import {
  selectSelectedScreenshare,
  selectShowAllScreens,
} from 'features/screenshare/screenshareSlice';
import { useContentWhiteboard } from 'features/content-whiteboard/useContentWhiteboard';
import { selectGridPanel } from 'features/layout/features/content/contentSlice';
import { selectContentLibraryOpenedFile } from 'features/content-library/contentLibrarySlice';
import { CONTENT_BOARD_SUPPORTED_FILE_TYPES } from 'features/content-whiteboard/constants';
import { selectShowScrensharePreview } from 'features/layout/features/config/configSlice';
import { useIsShareEntireScreen } from 'features/streaming/hooks/useIsShareEntireScreen';
import { selectLocalUserId } from 'features/users/usersSlice';
import { userAgentDetails } from 'utils/userAgentDetails';

export const useContentBoardPanelControl = () => {
  const { t } = useTranslation('content-whiteboard');

  const currentGridPanel = useAppSelector(selectGridPanel);
  const openedFile = useAppSelector(selectContentLibraryOpenedFile);
  const showScreensharePreview = useAppSelector(selectShowScrensharePreview);
  const selectedScreenshare = useAppSelector(selectSelectedScreenshare);
  const localUserId = useAppSelector(selectLocalUserId);
  const showAllScreens = useAppSelector(selectShowAllScreens);

  const isShareEntireScreen = useIsShareEntireScreen();
  const { isFeatureAvailable, canEditContent } = useContentWhiteboard();

  const { browser } = userAgentDetails();
  const isFirefox = browser.name?.toLowerCase() === 'firefox';

  const controlEnabled = useMemo(() => {
    if (!isFeatureAvailable || !canEditContent) {
      return false;
    }

    if (currentGridPanel === 'screenshare') {
      if (showAllScreens) {
        return false;
      }

      return (
        showScreensharePreview ||
        (!isShareEntireScreen && !isFirefox) ||
        selectedScreenshare?.userId !== localUserId
      );
    }

    if (currentGridPanel === 'contentLibrary') {
      return Boolean(openedFile && CONTENT_BOARD_SUPPORTED_FILE_TYPES.includes(openedFile.type));
    }

    return false;
  }, [
    currentGridPanel,
    openedFile,
    showScreensharePreview,
    selectedScreenshare,
    localUserId,
    showAllScreens,
    isFeatureAvailable,
    canEditContent,
    isShareEntireScreen,
    isFirefox,
  ]);

  return { controlEnabled, tooltip: t('panel.tooltip') };
};
